// MemberDocuments.tsx - Section documents avec responsive design
import React, { useState } from 'react';
import { MembershipPermissions } from '../../types/adhesion.types';
import {
    FileText,
    Download,
    Lock,
    Unlock,
    BookOpen,
    Shield,
    Users,
    Calendar
} from 'lucide-react';

interface Document {
    id: string;
    title: string;
    description: string;
    type: 'public' | 'private' | 'official' | 'internal';
    fileType: 'pdf' | 'doc' | 'image';
    size: string;
    icon: React.ReactNode;
    downloadUrl?: string;
    requiresPermission?: keyof MembershipPermissions;
    isNew?: boolean;
}

interface MemberDocumentsProps {
    memberData: any;
    permissions: MembershipPermissions;
}

const MemberDocuments: React.FC<MemberDocumentsProps> = ({ memberData, permissions }) => {
    const [selectedCategory, setSelectedCategory] = useState<string>('all');

    // Liste des documents disponibles
    const documents: Document[] = [
        {
            id: 'statute',
            title: 'Statuts de l\'UDB',
            description: 'Document fondateur de l\'Union Démocratique des Bâtisseurs',
            type: 'public',
            fileType: 'pdf',
            size: '2.1 MB',
            icon: <BookOpen className="w-5 h-5 text-blue-600" />,
            downloadUrl: '/documents/statuts-udb.pdf'
        },
        {
            id: 'program',
            title: 'Programme politique 2024',
            description: 'Vision et propositions pour le Gabon de demain',
            type: 'public',
            fileType: 'pdf',
            size: '5.3 MB',
            icon: <FileText className="w-5 h-5 text-green-600" />,
            downloadUrl: '/documents/programme-2024.pdf'
        },
        {
            id: 'member-guide',
            title: 'Guide du membre',
            description: 'Tout ce qu\'il faut savoir sur votre rôle de membre UDB',
            type: 'private',
            fileType: 'pdf',
            size: '1.8 MB',
            icon: <Users className="w-5 h-5 text-purple-600" />,
            downloadUrl: '/documents/guide-membre.pdf',
            requiresPermission: 'canAccessPrivateContent'
        },
        {
            id: 'internal-rules',
            title: 'Règlement intérieur',
            description: 'Règles de fonctionnement interne de l\'organisation',
            type: 'official',
            fileType: 'pdf',
            size: '950 KB',
            icon: <Shield className="w-5 h-5 text-red-600" />,
            downloadUrl: '/documents/reglement-interieur.pdf',
            requiresPermission: 'canDownloadOfficialDocuments'
        },
        {
            id: 'electoral-program',
            title: 'Programme électoral - Législatives 2024',
            description: 'Nos propositions pour les élections législatives',
            type: 'official',
            fileType: 'pdf',
            size: '4.2 MB',
            icon: <Calendar className="w-5 h-5 text-orange-600" />,
            downloadUrl: '/documents/programme-legislatives.pdf',
            requiresPermission: 'canDownloadOfficialDocuments',
            isNew: true
        },
        {
            id: 'member-card-form',
            title: 'Formulaire d\'adhésion',
            description: 'Version PDF de votre formulaire d\'adhésion signé',
            type: 'internal',
            fileType: 'pdf',
            size: 'Variable',
            icon: <Download className="w-5 h-5 text-indigo-600" />,
            downloadUrl: '#generate-pdf'
        }
    ];

    // Filtrer les documents selon les permissions
    const getAvailableDocuments = () => {
        return documents.filter(doc => {
            if (!doc.requiresPermission) return true;
            return permissions[doc.requiresPermission];
        });
    };

    // Filtrer par catégorie
    const getFilteredDocuments = () => {
        const availableDocs = getAvailableDocuments();
        if (selectedCategory === 'all') return availableDocs;
        return availableDocs.filter(doc => doc.type === selectedCategory);
    };

    // Catégories de documents
    const categories = [
        { id: 'all', label: 'Tous les documents', icon: <FileText className="w-4 h-4" /> },
        { id: 'public', label: 'Documents publics', icon: <Unlock className="w-4 h-4" /> },
        { id: 'private', label: 'Espace membre', icon: <Users className="w-4 h-4" /> },
        { id: 'official', label: 'Documents officiels', icon: <Shield className="w-4 h-4" /> },
        { id: 'internal', label: 'Documents personnels', icon: <Lock className="w-4 h-4" /> }
    ];

    // Obtenir la couleur selon le type de document
    const getDocumentColor = (type: string) => {
        switch (type) {
            case 'public': return 'border-green-200 bg-green-50';
            case 'private': return 'border-blue-200 bg-blue-50';
            case 'official': return 'border-red-200 bg-red-50';
            case 'internal': return 'border-purple-200 bg-purple-50';
            default: return 'border-gray-200 bg-white';
        }
    };

    const getDocumentBadgeColor = (type: string) => {
        switch (type) {
            case 'public': return 'bg-green-100 text-green-800';
            case 'private': return 'bg-blue-100 text-blue-800';
            case 'official': return 'bg-red-100 text-red-800';
            case 'internal': return 'bg-purple-100 text-purple-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const handleDownload = (document: Document) => {
        if (document.downloadUrl === '#generate-pdf') {
            // Logique pour générer le PDF d'adhésion
            console.log('Génération du PDF d\'adhésion...');
        } else if (document.downloadUrl) {
            // Téléchargement direct
            window.open(document.downloadUrl, '_blank');
        }
    };

    const filteredDocuments = getFilteredDocuments();

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-purple-100 rounded-lg">
                        <FileText className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Mes documents</h1>
                        <p className="text-gray-600">Accédez à tous vos documents UDB</p>
                    </div>
                </div>

                {/* Statistiques */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <div className="text-lg font-bold text-gray-900">{getAvailableDocuments().length}</div>
                        <div className="text-sm text-gray-600">Documents disponibles</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <div className="text-lg font-bold text-gray-900">
                            {getAvailableDocuments().filter(d => d.type === 'public').length}
                        </div>
                        <div className="text-sm text-gray-600">Publics</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <div className="text-lg font-bold text-gray-900">
                            {getAvailableDocuments().filter(d => d.requiresPermission).length}
                        </div>
                        <div className="text-sm text-gray-600">Privés</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <div className="text-lg font-bold text-gray-900">
                            {getAvailableDocuments().filter(d => d.isNew).length}
                        </div>
                        <div className="text-sm text-gray-600">Nouveaux</div>
                    </div>
                </div>
            </div>

            {/* Filtres par catégorie */}
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
                <div className="flex flex-wrap gap-2">
                    {categories.map((category) => {
                        const count = category.id === 'all' 
                            ? getAvailableDocuments().length 
                            : getAvailableDocuments().filter(d => d.type === category.id).length;
                        
                        if (count === 0 && category.id !== 'all') return null;
                        
                        return (
                            <button
                                key={category.id}
                                onClick={() => setSelectedCategory(category.id)}
                                className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                    selectedCategory === category.id
                                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                                {category.icon}
                                <span>{category.label}</span>
                                <span className="bg-white px-2 py-0.5 rounded-full text-xs">{count}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Message si pas de documents dans la catégorie */}
            {filteredDocuments.length === 0 && (
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Aucun document dans cette catégorie
                    </h3>
                    <p className="text-gray-600">
                        Sélectionnez une autre catégorie ou vérifiez vos permissions d'accès.
                    </p>
                </div>
            )}

            {/* Liste des documents */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {filteredDocuments.map((document) => (
                    <div key={document.id} className={`rounded-lg border p-4 ${getDocumentColor(document.type)}`}>
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center space-x-3">
                                <div className="p-2 bg-white rounded-lg shadow-sm">
                                    {document.icon}
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center space-x-2">
                                        <h3 className="font-semibold text-gray-900 text-sm">{document.title}</h3>
                                        {document.isNew && (
                                            <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full font-medium">
                                                Nouveau
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-600 mt-1">{document.description}</p>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDocumentBadgeColor(document.type)}`}>
                                    {document.type === 'public' && 'Public'}
                                    {document.type === 'private' && 'Privé'}
                                    {document.type === 'official' && 'Officiel'}
                                    {document.type === 'internal' && 'Personnel'}
                                </span>
                                <span className="text-xs text-gray-500">
                                    {document.fileType.toUpperCase()} • {document.size}
                                </span>
                            </div>

                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={() => handleDownload(document)}
                                    className="flex items-center space-x-1 bg-white text-gray-700 px-3 py-1 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium border border-gray-200"
                                >
                                    <Download className="w-3 h-3" />
                                    <span>Télécharger</span>
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Message d'amélioration si permissions limitées */}
            {!permissions.canDownloadOfficialDocuments && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <div className="flex items-start space-x-3">
                        <div className="p-2 bg-yellow-100 rounded-lg">
                            <Lock className="w-5 h-5 text-yellow-600" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-yellow-800">
                                Débloquez plus de documents
                            </h3>
                            <p className="text-yellow-700 mt-1">
                                Devenez membre complet pour accéder aux documents officiels et ressources exclusives.
                            </p>
                            <button className="mt-3 bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors font-medium text-sm">
                                En savoir plus
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Aide et support */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    💡 Besoin d'aide avec les documents ?
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Documents manquants ?</h4>
                        <p className="text-sm text-gray-600 mb-3">
                            Si vous ne trouvez pas un document, vérifiez vos permissions ou contactez le support.
                        </p>
                        <a
                            href="mailto:support@udb.ga"
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                            Contacter le support →
                        </a>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Problème de téléchargement ?</h4>
                        <p className="text-sm text-gray-600 mb-3">
                            Vérifiez votre connexion internet et réessayez. Les documents sont au format PDF.
                        </p>
                        <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                            Voir l'aide technique →
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MemberDocuments;